import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import {
  Backdrop,
  Box,
  DotProgress,
  FlexBox,
  FormDialog,
  SelectDropdown,
  Typography,
  color
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { actions as configurationsActions } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { getSortedList } from 'utils/stringFormat';
import Controls from '../components/Controls';
import ManageImageOverlay from '../components/ManageImageOverlay';
import PreviewImageOverlay from '../components/PreviewImageOverlay';
import MediaTable from '../components/MediaTable';
import { KIOSK_GRAPHIC_SLOTS } from '../kioskGraphics';
import { MediaType, MediaResponse, MediaConfigurationProps, MediaTypes } from '../types';
import { getMediaTypeLabel } from '../utils';

const OtherImagesLayout = (props: MediaConfigurationProps) => {
  const { configuration } = props;
  const { id, configuration: configColumn, ticketing } = configuration;

  const [updateConfiguration, getMedia, resetAction, deleteMedia, downloadMedia] = useActions([
    configurationsActions.updateConfiguration,
    mediaActions.getMedia,
    mediaActions.resetAction,
    mediaActions.deleteMedia,
    mediaActions.downloadMedia
  ]);

  const { error, pending, successful } = useSelector(mediaSelectors.selectAction);
  const otherImages = useSelector(mediaSelectors.selectOtherImages);

  const [addParams, setAddParams] = useState<boolean>(false);
  const [deleteParams, setDeleteParams] = useState<MediaResponse | null>(null);
  const [downloadParams, setDownloadParams] = useState<MediaResponse | null>(null);
  const [editParams, setEditParams] = useState<MediaResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [imagePreviewSrc, setImagePreviewSrc] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  // promo code or kiosk graphic slot key the add/edit overlay is for
  const [selectedResourceName, setSelectedResourceName] = useState<string | null>(null);

  const promoCodeCustomOptions = get(ticketing, 'promoCode.selection.customOptions', []);
  const promoCodeManualEntry = get(ticketing, 'promoCode.selection.manualEntry.enabled', false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const promoCodesList: string[] = promoCodeCustomOptions.map((customOption: any) => customOption.code);
  if (promoCodeManualEntry) promoCodesList.push('Manual Entry');

  const mediaTypeLabelLowercase = getMediaTypeLabel(mediaType?.value as MediaTypes, true);

  useEffect(() => {
    if (id && mediaType && mediaType.value) {
      // reset type dropdown if background is selected, and flowTemplate is not fair
      const { flowTemplate } = configColumn;
      if (flowTemplate !== 'fair' && mediaType.value === 'background-image') {
        setMediaType(null);
      } else {
        getMedia({ type: mediaType.value, configurationId: id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, id]);

  useEffect(() => {
    if (downloadParams) downloadMedia(downloadParams.id);
  }, [downloadParams]);

  useEffect(() => {
    if (successful && id && mediaType && mediaType.value) {
      if (downloadParams) {
        // After downloading, reset the params and action without refreshing media or updating the configuration.
        setDownloadParams(null);
        resetAction();
        return;
      }

      getMedia({ type: mediaType.value, configurationId: id });
      // updateConfig with nothing so the kiosk(s) fetch the updated images
      updateConfiguration({ id });
      resetAction();
      setAddParams(false);
      setEditParams(null);
      setSelectedResourceName(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      resetAction();
      setDownloadParams(null);
    }
  }, [error]);

  // options for the type dropdown
  const getMediaTypeList = () => {
    const mediaTypeList: MediaType[] = [
      { name: 'Inactive Page Image', value: 'inactive-image', path: 'inactive_images' },
      { name: 'Splash Page Image', value: 'splash-image', path: 'splash_images' },
      { name: 'Client Logo Image - Small', value: 'logo-image', path: 'logo_images' },
      { name: 'Client Logo Image - Large', value: 'logo-image-large', path: 'logo_images' },
      { name: 'Promo Code Images', value: 'promo-code-image', path: 'promo_code_images' },
      { name: 'Kiosk Graphics', value: 'kiosk-graphic', path: 'kiosk_graphics' }
    ];
    if (configColumn.flowTemplate && configColumn.flowTemplate === 'fair') {
      mediaTypeList.push({ name: 'Background Image', value: 'background-image', path: 'background_images' });
    }

    return getSortedList(mediaTypeList, 'name');
  };

  const onDeleteConfirm = () => {
    deleteMedia((deleteParams as MediaResponse).id);
    setDeleteParams(null);
  };

  const onEditClick = (mediaRecord?: MediaResponse, resourceName?: string) => {
    if (resourceName) setSelectedResourceName(resourceName);

    if (mediaRecord) setEditParams(mediaRecord);
    else setAddParams(true);
  };

  const getMediaCount = (): number => {
    switch (mediaType?.value) {
      case MediaTypes.BACKGROUND_IMAGE:
        return get(otherImages, 'background.length', 0);
      case MediaTypes.INACTIVE_IMAGE:
        return get(otherImages, 'inactive.length', 0);
      case MediaTypes.LOGO_IMAGE:
        return get(otherImages, 'logo.length', 0);
      case MediaTypes.LOGO_IMAGE_LARGE:
        return get(otherImages, 'logoLarge.length', 0);
      case MediaTypes.PROMO_CODE_IMAGE:
        return get(otherImages, 'promoCode.length', 0);
      case MediaTypes.KIOSK_GRAPHIC:
        return get(otherImages, 'kioskGraphic.length', 0);
      case MediaTypes.SPLASH_IMAGE:
        return get(otherImages, 'splash.length', 0);
      default:
        return 0;
    }
  };

  const getMediaRecordsForSelectedType = (): MediaResponse[] => {
    switch (mediaType?.value) {
      case MediaTypes.BACKGROUND_IMAGE:
        return get(otherImages, 'background', []);
      case MediaTypes.INACTIVE_IMAGE:
        return get(otherImages, 'inactive', []);
      case MediaTypes.LOGO_IMAGE:
        return get(otherImages, 'logo', []);
      case MediaTypes.LOGO_IMAGE_LARGE:
        return get(otherImages, 'logoLarge', []);
      case MediaTypes.PROMO_CODE_IMAGE:
        return get(otherImages, 'promoCode', []);
      case MediaTypes.KIOSK_GRAPHIC:
        return get(otherImages, 'kioskGraphic', []);
      case MediaTypes.SPLASH_IMAGE:
        return get(otherImages, 'splash', []);
      default:
        return [];
    }
  };

  return (
    <>
      {/* LOADING OVERLAY */}
      {pending && (
        <Backdrop open={pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}

      {/* MAIN CONTENT */}
      <FlexBox direction="column" noWrap>
        <FlexBox align="center" margin="0 0 6" direction="row">
          <Typography fontSize={20} fontWeight={700} sx={{ marginRight: '20px' }}>
            <Box width={180}> Type </Box>
          </Typography>
          <SelectDropdown
            items={getMediaTypeList()}
            itemToString={(item: MediaType) => (item ? item.name : '')}
            selectedItem={mediaType as MediaType}
            onChange={(selectedType) => setMediaType(selectedType)}
            placeholder="Select a type"
            radius="round"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: mediaType ? '#000' : '#9E9E9E'
              }
            }}
          />
        </FlexBox>
        {mediaType && (
          <>
            {/* COMMON CONTROLS (Add) */}
            <Controls
              configuration={configuration}
              mediaCount={getMediaCount()}
              mediaType={mediaType.value as MediaTypes}
              onAddClick={() => setAddParams(true)}
            />
            {/* MEDIA TABLE */}
            <MediaTable
              mediaRecords={getMediaRecordsForSelectedType()}
              mediaType={mediaType.value as MediaTypes}
              onDeleteClick={(mediaRecord) => setDeleteParams(mediaRecord)}
              onDownloadClick={(mediaRecord) => setDownloadParams(mediaRecord)}
              onEditClick={(mediaRecord, resourceName) => onEditClick(mediaRecord, resourceName)}
              onPreviewClick={(mediaSource) => setImagePreviewSrc(mediaSource)}
              promoCodes={mediaType.value === MediaTypes.PROMO_CODE_IMAGE ? promoCodesList : undefined}
              kioskGraphics={mediaType.value === MediaTypes.KIOSK_GRAPHIC ? KIOSK_GRAPHIC_SLOTS : undefined}
            />
          </>
        )}
      </FlexBox>

      {/* DELETE OVERLAY */}
      {deleteParams && (
        <FormDialog
          cancelButtonAction={() => setDeleteParams(null)}
          cancelButtonText="Cancel"
          confirmButtonAction={onDeleteConfirm}
          confirmButtonText="Delete"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {`Are you sure you want to delete this ${mediaTypeLabelLowercase}?`}
            {mediaType?.value === MediaTypes.KIOSK_GRAPHIC && ' The default image will be shown instead.'}
          </Typography>
        </FormDialog>
      )}

      {/* ADD/EDIT IMAGE OVERLAY */}
      {(addParams || editParams) && (
        <ManageImageOverlay
          action={addParams ? 'Select' : 'Edit'}
          configurationId={id}
          mediaRecord={editParams ? editParams : undefined}
          mediaType={(mediaType as MediaType).value as MediaTypes}
          onClose={() => {
            setAddParams(false);
            setEditParams(null);
            setSelectedResourceName(null);
          }}
          resourceName={selectedResourceName ? selectedResourceName : undefined}
        />
      )}

      {/* PREVIEW IMAGE OVERLAY */}
      {imagePreviewSrc && <PreviewImageOverlay imageSrc={imagePreviewSrc} onClose={() => setImagePreviewSrc(null)} />}

      {/* ERROR OVERLAY */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
    </>
  );
};

export default OtherImagesLayout;
